import { ImpersonationSwitcher } from '@/components/impersonation-switcher';
import { shouldRenderItem } from '@/components/should-render-item';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/tailwind-utils';
import { ExternalNavLink, NavLink, NavLinkDefinition } from '@/routes/_layout/-components/nav-link';
import { UserAvatarMenu } from '@/routes/_layout/-components/user-avatar-menu';
import { NAV } from '@airreading/automation-test/test-ids';
import { type LoggedInUser } from '@airreading/common/data';
import { isAirAdmin } from '@airreading/common';
import { Link } from '@tanstack/react-router';
import { PersonStanding } from 'lucide-react';
import { ReactNode } from 'react';

interface SidebarContentProps {
  matchedNavLinks: NavLinkDefinition[];
  loggedInUser: LoggedInUser;
  actingUser: LoggedInUser | undefined;
  userIsStudent: boolean;
  isAdmin: boolean;
  /**
   * Optional slot rendered next to the logo. Used by the mobile sheet to render
   * the close (ArrowLeft) button.
   */
  headerSlot?: ReactNode;
  /**
   * Called whenever a nav item is activated. Used by the mobile sheet to close
   * the drawer on selection.
   */
  onNavigate?: () => void;
}

export const SidebarContent = ({
  matchedNavLinks,
  loggedInUser,
  actingUser,
  userIsStudent,
  isAdmin,
  headerSlot,
  onNavigate,
}: SidebarContentProps) => {
  return (
    <>
      <div className="container px-0 py-2 grid overflow-auto">
        <div className="container p-2 flex items-center justify-between mb-4 gap-1">
          <div className="flex items-center justify-start gap-1">
            <Link to="/" className="flex items-center justify-center" data-testid={NAV.logo} onClick={onNavigate}>
              {userIsStudent ? (
                <img src="/white-logo.svg" alt="Air Reading Logo" className="h-10" />
              ) : (
                <img src="/logo.svg" alt="Air Reading Logo" className="h-10" />
              )}
            </Link>
            {isAdmin && (
              <div className="text-xs text-gray-400 h-[75%] flex items-center border-l border-border pl-2">
                Admin portal
              </div>
            )}
          </div>
          {headerSlot}
        </div>
        {matchedNavLinks.map((definition, index) => {
          if ('isDivider' in definition) {
            const visibleItem = actingUser
              ? shouldRenderItem(
                  {
                    ...actingUser,
                    fullName: actingUser.fullNameOrEmail,
                  },
                  definition.shouldRender,
                )
              : true;
            if (!visibleItem) return null;

            return <hr key={`divider-${index}`} className="my-2 border-t border-border" />;
          }

          if ('externalURL' in definition) {
            return <ExternalNavLink key={definition.label} linkDefinition={definition} onNavigate={onNavigate} />;
          }

          return (
            <NavLink
              key={`${definition.label}-${definition.linkProps.to}`}
              linkDefinition={definition}
              onNavigate={onNavigate}
            />
          );
        })}
      </div>
      <div className="container p-2 flex items-center gap-x-2 justify-between border-t border-border">
        <UserAvatarMenu />
        {isAirAdmin(loggedInUser) && (
          <ImpersonationSwitcher
            trigger={
              <Button
                icon={PersonStanding}
                variant={'ghost'}
                className={cn('h-[4rem] w-[4rem] rounded-full', {
                  '[&>*]:stroke-white hover:bg-[#d23841]': userIsStudent,
                })}
              />
            }
          />
        )}
      </div>
    </>
  );
};
